/**
 * Email-safe HTML generation utilities
 * Uses table-based layouts and inline styles that work in all email clients
 */

/**
 * Generate email-safe HTML content for pairings email
 */
export const generateEmailSafeHtml = ({ 
  eventDetails, 
  pairings = [], 
  eventPlayers = [], 
  weatherData = null,
  logoBase64 = null,
  customMessage = null
}) => {
  const formatTime = (timeString) => {
    if (!timeString) return 'TBD';
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'TBD';
    try {
      let date;
      if (dateString.includes('-')) {
        const [year, month, day] = dateString.split('-');
        date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      } else {
        date = new Date(dateString);
      }
      if (isNaN(date.getTime())) return dateString;
      
      return date.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      return dateString;
    }
  };

  // Generate weather section - enhanced with more data
  const weatherSection = weatherData ? `
    <table width="100%" cellpadding="0" cellspacing="0" style="margin-top: 20px;">
      <tr>
        <td>
          <h3 style="color: #1F2937; font-size: 20px; font-weight: bold; margin: 0 0 12px 0;">🌤️ Weather Forecast</h3>
        </td>
      </tr>
      <tr>
        <td style="background-color: #EBF8FF; border: 1px solid #BAE6FD; padding: 15px;">
          <table width="100%" cellpadding="0" cellspacing="10">
            <tr>
              <td width="33%" style="text-align: center;">
                <div style="background-color: white; padding: 12px; border-radius: 6px;">
                  <div style="font-size: 24px; margin-bottom: 5px;">🌡️</div>
                  <div style="color: #075985; font-size: 12px; font-weight: bold; margin-bottom: 3px;">TEE TIME</div>
                  <div style="color: #0369A1; font-size: 22px; font-weight: bold;">${weatherData.teeTimeTemp || 'N/A'}°F</div>
                </div>
              </td>
              <td width="33%" style="text-align: center;">
                <div style="background-color: white; padding: 12px; border-radius: 6px;">
                  <div style="font-size: 24px; margin-bottom: 5px;">🌡️</div>
                  <div style="color: #075985; font-size: 12px; font-weight: bold; margin-bottom: 3px;">+4 HOURS</div>
                  <div style="color: #0369A1; font-size: 22px; font-weight: bold;">${weatherData.laterTemp || 'N/A'}°F</div>
                </div>
              </td>
              <td width="33%" style="text-align: center;">
                <div style="background-color: white; padding: 12px; border-radius: 6px;">
                  <div style="font-size: 24px; margin-bottom: 5px;">☔</div>
                  <div style="color: #075985; font-size: 12px; font-weight: bold; margin-bottom: 3px;">RAIN CHANCE</div>
                  <div style="color: #0369A1; font-size: 22px; font-weight: bold;">${weatherData.rainChance || 'N/A'}%</div>
                </div>
              </td>
            </tr>
            <tr>
              <td colspan="3" style="padding-top: 10px;">
                <table width="100%" cellpadding="8" cellspacing="0" style="background-color: white; border-radius: 6px;">
                  <tr>
                    <td width="25%" style="text-align: center; border-right: 1px solid #E5E7EB;">
                      <div style="color: #075985; font-size: 11px; font-weight: bold;">💨 WIND</div>
                      <div style="color: #0369A1; font-size: 16px; font-weight: bold;">${weatherData.windSpeed || 'N/A'} mph</div>
                    </td>
                    <td width="25%" style="text-align: center; border-right: 1px solid #E5E7EB;">
                      <div style="color: #075985; font-size: 11px; font-weight: bold;">💧 HUMIDITY</div>
                      <div style="color: #0369A1; font-size: 16px; font-weight: bold;">${weatherData.humidity || 'N/A'}%</div>
                    </td>
                    <td width="25%" style="text-align: center; border-right: 1px solid #E5E7EB;">
                      <div style="color: #075985; font-size: 11px; font-weight: bold;">☁️ CLOUDS</div>
                      <div style="color: #0369A1; font-size: 16px; font-weight: bold;">${weatherData.cloudCover || 'N/A'}%</div>
                    </td>
                    <td width="25%" style="text-align: center;">
                      <div style="color: #075985; font-size: 11px; font-weight: bold;">☀️ UV INDEX</div>
                      <div style="color: #0369A1; font-size: 16px; font-weight: bold;">${weatherData.uvIndex || 'N/A'}</div>
                    </td>
                  </tr>
                </table>
              </td>
            </tr>
          </table>
        </td>
      </tr>
    </table>
  ` : '';

  // Generate pairings section - full width with better layout
  let pairingsSection = '';
  if (pairings && pairings.length > 0) {
    pairingsSection = `
      <table width="100%" cellpadding="0" cellspacing="0" style="margin-top: 20px;">
        <tr>
          <td>
            <h3 style="color: #1F2937; font-size: 20px; font-weight: bold; margin: 0 0 12px 0;">⛳ Pairings</h3>
          </td>
        </tr>
        <tr>
          <td style="background-color: #F9FAFB; border: 1px solid #E5E7EB; padding: 10px;">
            <table width="100%" cellpadding="0" cellspacing="8">
              <tr>
    `;
    
    pairings.forEach((group, index) => {
      if (index % 2 === 0 && index > 0) {
        pairingsSection += '</tr><tr>';
      }
      
      // Alternate group colors
      const groupColors = ['#4F46E5', '#7C3AED'];
      const groupColor = groupColors[index % groupColors.length];
      
      pairingsSection += `
        <td width="50%" valign="top">
          <table width="100%" cellpadding="0" cellspacing="0" style="background-color: white; border: 1px solid ${groupColor};">
            <tr>
              <td style="background-color: ${groupColor}; color: white; padding: 10px; font-weight: bold; font-size: 16px; text-align: center;">
                Group ${index + 1}
              </td>
            </tr>
      `;
      
      group.forEach((player, playerIndex) => {
        const playerData = eventPlayers.find((p) => p.player_id === player.player_id);
        const quota = playerData?.quota || '';
        
        let quotaColor = '#059669';
        let quotaBg = '#ECFDF5';
        let teeIndicator = '';
        
        if (quota >= 31) {
          quotaColor = '#DC2626';
          quotaBg = '#FEF2F2';
          teeIndicator = '← ←';
        } else if (quota >= 26) {
          quotaColor = '#EA580C';
          quotaBg = '#FFF7ED';
          teeIndicator = '←';
        }
        
        const borderBottom = playerIndex < group.length - 1 ? 'border-bottom: 1px solid #E5E7EB;' : '';
        const bgColor = playerIndex % 2 === 0 ? '#FFFFFF' : '#F9FAFB';
        
        pairingsSection += `
          <tr>
            <td style="padding: 10px 12px; ${borderBottom} background-color: ${bgColor};">
              <table width="100%" cellpadding="0" cellspacing="0">
                <tr>
                  <td style="color: #374151; font-size: 15px; font-weight: 500;">${player.name}</td>
                  <td align="right" style="white-space: nowrap;">
                    ${quota ? `
                    ${teeIndicator ? `<span style="color: ${quotaColor}; font-size: 13px; margin-right: 6px; font-weight: bold;">${teeIndicator}</span>` : ''}
                    <span style="color: ${quotaColor}; background-color: ${quotaBg}; padding: 4px 10px; font-size: 15px; font-weight: bold;">
                      ${quota}
                    </span>
                    ` : ''}
                  </td>
                </tr>
              </table>
            </td>
          </tr>
        `;
      });
      
      pairingsSection += `
          </table>
        </td>
      `;
    });
    
    // Fill empty cell if odd number of groups
    if (pairings.length % 2 === 1) {
      pairingsSection += '<td width="50%">&nbsp;</td>';
    }
    
    pairingsSection += `
              </tr>
            </table>
          </td>
        </tr>
      </table>
    `;
  }

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Gulf Coast Hackers League - ${eventDetails.course_name}</title>
</head>
<body style="margin: 0; padding: 0; font-family: Arial, sans-serif; line-height: 1.4; color: #333333; background-color: #f5f5f5;">
  <table width="100%" cellpadding="0" cellspacing="0" style="background-color: #f5f5f5;">
    <tr>
      <td align="center">
        <table width="700" cellpadding="0" cellspacing="0" style="background-color: white; margin: 20px auto;">
          
          <!-- Header -->
          <tr>
            <td style="background-color: #4F46E5; padding: 25px;">
              <table width="100%" cellpadding="0" cellspacing="0">
                <tr>
                  ${logoBase64 ? `
                  <td width="80" align="center">
                    <img src="data:image/png;base64,${logoBase64}" alt="GCHL Logo" width="80" height="80" style="display: block;">
                  </td>
                  <td width="20">&nbsp;</td>
                  ` : ''}
                  <td align="${logoBase64 ? 'left' : 'center'}">
                    <h1 style="color: white; font-size: 26px; margin: 0 0 8px 0; font-weight: bold;">
                      ${eventDetails.course_name || 'Golf Event'}
                    </h1>
                    <p style="color: white; font-size: 18px; margin: 0;">
                      ${formatDate(eventDetails.date)} • ${formatTime(eventDetails.tee_time)}
                    </p>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
          
          <!-- Content -->
          <tr>
            <td style="padding: 20px;">
              
              ${customMessage ? `
              <!-- Custom Message -->
              <div style="background-color: #EBF8FF; border: 1px solid #BAE6FD; padding: 15px; margin-bottom: 20px; border-radius: 6px;">
                <p style="margin: 0; color: #0369A1; font-size: 16px; line-height: 1.5;">
                  ${customMessage.split('\n').map(line => `${line}`).join('<br>')}
                </p>
              </div>
              ` : ''}
              
              <!-- Event Details -->
              <h3 style="color: #1F2937; font-size: 20px; font-weight: bold; margin: 0 0 12px 0;">📅 Event Information</h3>
              <table width="100%" cellpadding="15" cellspacing="0" style="background-color: #F9FAFB; border: 1px solid #E5E7EB;">
                <tr>
                  <td width="50%" style="font-size: 16px; line-height: 1.8; vertical-align: top;">
                    <strong style="color: #374151;">Date:</strong> <span style="color: #111827;">${formatDate(eventDetails.date)}</span><br>
                    <strong style="color: #374151;">1st Tee Time:</strong> <span style="color: #111827;">${formatTime(eventDetails.tee_time)}</span><br>
                    ${eventDetails.cost ? `<strong style="color: #374151;">Cost:</strong> <span style="color: #111827;">$${Number(eventDetails.cost).toFixed(2)}</span><br>` : ''}
                  </td>
                  <td width="50%" style="font-size: 16px; line-height: 1.8; vertical-align: top;">
                    ${eventDetails.front_tee && eventDetails.back_tee ? `<strong style="color: #374151;">Tees:</strong> <span style="color: #111827;">${eventDetails.front_tee} / ${eventDetails.back_tee}</span><br>` : ''}
                    ${eventDetails.total_yardage ? `<strong style="color: #374151;">Total Yardage:</strong> <span style="color: #111827;">${eventDetails.total_yardage} yards</span><br>` : ''}
                    ${eventDetails.notes ? `<strong style="color: #374151;">Notes:</strong> <span style="color: #111827;">${eventDetails.notes}</span><br>` : ''}
                  </td>
                </tr>
                ${eventDetails.course_address ? `
                <tr>
                  <td colspan="2" style="font-size: 15px; line-height: 1.6; padding-top: 8px; border-top: 1px solid #E5E7EB;">
                    <strong style="color: #374151;">Address:</strong> 
                    <a href="https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(eventDetails.course_address)}" 
                       style="color: #4F46E5; text-decoration: none; font-weight: 500;">
                      ${eventDetails.course_address}
                    </a>
                  </td>
                </tr>
                ` : ''}
                ${eventDetails.website ? `
                <tr>
                  <td colspan="2" style="font-size: 15px; line-height: 1.6; padding-top: 8px; border-top: 1px solid #E5E7EB;">
                    <strong style="color: #374151;">Course Website:</strong> 
                    <a href="${eventDetails.website}" style="color: #4F46E5; text-decoration: none; font-weight: 500;">
                      ${eventDetails.website}
                    </a>
                  </td>
                </tr>
                ` : ''}
              </table>
              
              ${pairingsSection}
              ${weatherSection}
              
              <!-- Signature -->
              <table width="100%" cellpadding="0" cellspacing="0" style="margin-top: 30px;">
                <tr>
                  <td>
                    <p style="margin: 0;">See you on the course!</p>
                    <p style="margin: 15px 0;">
                      Cheers,<br>
                      <strong>Gulf Coast Hackers League Admin</strong><br>
                      <a href="https://gulfcoasthackers.com" style="color: #4F46E5; text-decoration: none; font-style: italic;">gulfcoasthackers.com</a>
                    </p>
                  </td>
                </tr>
              </table>
              
            </td>
          </tr>
          
          <!-- Footer -->
          <tr>
            <td style="background-color: #6B7280; color: white; padding: 15px; text-align: center;">
              <p style="margin: 0; font-size: 14px;">🏌️ Gulf Coast Hackers League | Just Some Wankers Having Fun</p>
            </td>
          </tr>
          
        </table>
      </td>
    </tr>
  </table>
</body>
</html>`;
};

/**
 * Generate email-safe HTML for daily email
 */
export const generateDailyEmailSafeHtml = ({
  eventDetails,
  eventPlayers = [],
  upcomingEvents = [],
  weatherData = null,
  logoBase64 = null,
  customMessage = null
}) => {
  const formatTime = (timeString) => {
    if (!timeString) return 'TBD';
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'TBD';
    try {
      let date;
      if (dateString.includes('-')) {
        const [year, month, day] = dateString.split('-');
        date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      } else {
        date = new Date(dateString);
      }
      if (isNaN(date.getTime())) return dateString;
      
      return date.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      return dateString;
    }
  };

  // Generate signups section with detailed table
  let signupsSection = '';
  if (eventPlayers && eventPlayers.length > 0) {
    const sortedPlayers = [...eventPlayers].sort((a, b) => {
      const quotaA = a.quota || a.current_quota || 999;
      const quotaB = b.quota || b.current_quota || 999;
      return quotaA - quotaB; // Sort by quota (lowest first)
    });
    
    signupsSection = `
      <table width="100%" cellpadding="0" cellspacing="0" style="margin-top: 20px;">
        <tr>
          <td>
            <h3 style="color: #1F2937; font-size: 18px; font-weight: bold; margin: 0 0 10px 0;">
              👥 Current Signups (${eventPlayers.length} players)
            </h3>
          </td>
        </tr>
        <tr>
          <td>
            <table width="100%" cellpadding="0" cellspacing="0" style="background-color: white; border: 1px solid #E5E7EB; border-radius: 6px;">
              <!-- Header Row -->
              <tr style="background-color: #4F46E5;">
                <td width="10%" style="padding: 10px; text-align: center; color: white; font-weight: bold; font-size: 14px; border-right: 1px solid #6366F1;">#</td>
                <td width="60%" style="padding: 10px; color: white; font-weight: bold; font-size: 14px; border-right: 1px solid #6366F1;">Player Name</td>
                <td width="15%" style="padding: 10px; text-align: center; color: white; font-weight: bold; font-size: 14px; border-right: 1px solid #6366F1;">Quota</td>
                <td width="15%" style="padding: 10px; text-align: center; color: white; font-weight: bold; font-size: 14px;">FedUp Place</td>
              </tr>
    `;
    
    // Create rows for each player
    sortedPlayers.forEach((player, index) => {
      const quota = player.quota || player.current_quota || '';
      const fedupRank = player.season_paid ? (player.fedup_rank || '-') : '-';
      const isTop8 = player.season_paid && player.fedup_rank && parseInt(player.fedup_rank) <= 8;
      
      let quotaColor = '#059669';
      let quotaBg = '#ECFDF5';
      let backArrows = '';
      
      if (quota >= 31) {
        quotaColor = '#DC2626';
        quotaBg = '#FEF2F2';
        backArrows = '<span style="color: #DC2626; font-weight: bold; margin-right: 6px;">← ←</span>';
      } else if (quota >= 26) {
        quotaColor = '#EA580C';
        quotaBg = '#FFF7ED';
        backArrows = '<span style="color: #EA580C; font-weight: bold; margin-right: 6px;">←</span>';
      }
      
      const rowBg = isTop8 ? '#F0FDF4' : (index % 2 === 0 ? '#FFFFFF' : '#F9FAFB');
      const borderBottom = index < sortedPlayers.length - 1 ? 'border-bottom: 1px solid #E5E7EB;' : '';
      
      signupsSection += `
        <tr style="background-color: ${rowBg};">
          <td style="padding: 8px; text-align: center; font-weight: bold; color: #6B7280; ${borderBottom}">${index + 1}</td>
          <td style="padding: 8px; color: #374151; font-weight: 500; ${borderBottom}">
            <table width="100%" cellpadding="0" cellspacing="0">
              <tr>
                <td>${player.name}</td>
                <td align="right">${backArrows}</td>
              </tr>
            </table>
          </td>
          <td style="padding: 8px; text-align: center; ${borderBottom}">
            ${quota ? `<span style="color: ${quotaColor}; background-color: ${quotaBg}; padding: 3px 8px; border-radius: 4px; font-weight: bold;">${quota}</span>` : '-'}
          </td>
          <td style="padding: 8px; text-align: center; color: #374151; ${borderBottom}">${fedupRank}</td>
        </tr>
      `;
    });
    
    signupsSection += `
            </table>
          </td>
        </tr>
      </table>
    `;
  }

  // Generate upcoming events section
  let upcomingSection = '';
  if (upcomingEvents && upcomingEvents.length > 0) {
    // Filter out the current event from upcoming events
    const filteredEvents = upcomingEvents.filter(event => event.id !== eventDetails.id);
    
    if (filteredEvents.length > 0) {
      upcomingSection = `
        <table width="100%" cellpadding="0" cellspacing="0" style="margin-top: 20px;">
          <tr>
            <td>
              <h3 style="color: #1F2937; font-size: 18px; font-weight: bold; margin: 0 0 10px 0;">📅 Upcoming Events</h3>
            </td>
          </tr>
          <tr>
            <td>
              <table width="100%" cellpadding="8" cellspacing="0" style="background-color: white; border: 1px solid #E5E7EB; border-radius: 6px;">
      `;
      
      filteredEvents.forEach((event, index) => {
      if (index > 0) {
        upcomingSection += '<tr><td colspan="2" style="border-top: 1px solid #E5E7EB;"></td></tr>';
      }
      upcomingSection += `
        <tr>
          <td style="padding: 10px;">
            <strong style="color: #1F2937;">${event.course_name}</strong><br>
            <span style="color: #6B7280; font-size: 14px;">
              ${formatDate(event.date)} • ${formatTime(event.tee_time)}
            </span>
          </td>
        </tr>
      `;
      });
      
      upcomingSection += `
              </table>
            </td>
          </tr>
        </table>
      `;
    }
  }

  // Weather section - enhanced version
  const weatherSection = weatherData ? `
    <table width="100%" cellpadding="0" cellspacing="0" style="margin-top: 20px;">
      <tr>
        <td>
          <h3 style="color: #1F2937; font-size: 18px; font-weight: bold; margin: 0 0 10px 0;">🌤️ Weather Forecast</h3>
        </td>
      </tr>
      <tr>
        <td style="background-color: #EBF8FF; border: 1px solid #BAE6FD; padding: 12px; border-radius: 6px;">
          <table width="100%" cellpadding="0" cellspacing="8">
            <tr>
              <td width="33%" style="text-align: center;">
                <div style="background-color: white; padding: 10px; border-radius: 4px;">
                  <div style="font-size: 20px; margin-bottom: 3px;">🌡️</div>
                  <div style="color: #075985; font-size: 11px; font-weight: bold;">TEE TIME</div>
                  <div style="color: #0369A1; font-size: 18px; font-weight: bold;">${weatherData.teeTimeTemp || 'N/A'}°F</div>
                </div>
              </td>
              <td width="33%" style="text-align: center;">
                <div style="background-color: white; padding: 10px; border-radius: 4px;">
                  <div style="font-size: 20px; margin-bottom: 3px;">🌡️</div>
                  <div style="color: #075985; font-size: 11px; font-weight: bold;">+4 HOURS</div>
                  <div style="color: #0369A1; font-size: 18px; font-weight: bold;">${weatherData.laterTemp || 'N/A'}°F</div>
                </div>
              </td>
              <td width="33%" style="text-align: center;">
                <div style="background-color: white; padding: 10px; border-radius: 4px;">
                  <div style="font-size: 20px; margin-bottom: 3px;">☔</div>
                  <div style="color: #075985; font-size: 11px; font-weight: bold;">RAIN</div>
                  <div style="color: #0369A1; font-size: 18px; font-weight: bold;">${weatherData.rainChance || 'N/A'}%</div>
                </div>
              </td>
            </tr>
            <tr>
              <td colspan="3" style="padding-top: 8px;">
                <table width="100%" cellpadding="6" cellspacing="0" style="background-color: white; border-radius: 4px;">
                  <tr>
                    <td width="25%" style="text-align: center; border-right: 1px solid #E5E7EB;">
                      <strong style="color: #075985; font-size: 10px;">💨 WIND</strong><br>
                      <span style="color: #0369A1; font-weight: bold;">${weatherData.windSpeed || 'N/A'} mph</span>
                    </td>
                    <td width="25%" style="text-align: center; border-right: 1px solid #E5E7EB;">
                      <strong style="color: #075985; font-size: 10px;">💧 HUMIDITY</strong><br>
                      <span style="color: #0369A1; font-weight: bold;">${weatherData.humidity || 'N/A'}%</span>
                    </td>
                    <td width="25%" style="text-align: center; border-right: 1px solid #E5E7EB;">
                      <strong style="color: #075985; font-size: 10px;">☁️ CLOUDS</strong><br>
                      <span style="color: #0369A1; font-weight: bold;">${weatherData.cloudCover || 'N/A'}%</span>
                    </td>
                    <td width="25%" style="text-align: center;">
                      <strong style="color: #075985; font-size: 10px;">☀️ UV INDEX</strong><br>
                      <span style="color: #0369A1; font-weight: bold;">${weatherData.uvIndex || 'N/A'}</span>
                    </td>
                  </tr>
                </table>
              </td>
            </tr>
          </table>
        </td>
      </tr>
    </table>
  ` : '';

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Gulf Coast Hackers League - Daily Update</title>
</head>
<body style="margin: 0; padding: 0; font-family: Arial, sans-serif; line-height: 1.4; color: #333333; background-color: #f5f5f5;">
  <table width="100%" cellpadding="0" cellspacing="0" style="background-color: #f5f5f5;">
    <tr>
      <td align="center">
        <table width="700" cellpadding="0" cellspacing="0" style="background-color: white; margin: 20px auto;">
          
          <!-- Header -->
          <tr>
            <td style="background-color: #4F46E5; padding: 25px;">
              <table width="100%" cellpadding="0" cellspacing="0">
                <tr>
                  ${logoBase64 ? `
                  <td width="80" align="center">
                    <img src="data:image/png;base64,${logoBase64}" alt="GCHL Logo" width="80" height="80" style="display: block;">
                  </td>
                  <td width="20">&nbsp;</td>
                  ` : ''}
                  <td align="${logoBase64 ? 'left' : 'center'}">
                    <h1 style="color: white; font-size: 24px; margin: 0 0 8px 0; font-weight: bold;">
                      ${eventDetails.website ? 
                        `<a href="${eventDetails.website}" style="color: white; text-decoration: none;">${eventDetails.course_name}</a>` :
                        eventDetails.course_name
                      }
                    </h1>
                    <p style="color: white; font-size: 16px; margin: 0;">
                      ${formatDate(eventDetails.date)} • ${formatTime(eventDetails.tee_time)}
                    </p>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
          
          <!-- Content -->
          <tr>
            <td style="padding: 20px;">
              
              ${customMessage ? `
              <!-- Custom Message -->
              <div style="background-color: #EBF8FF; border: 1px solid #BAE6FD; padding: 15px; margin-bottom: 20px; border-radius: 6px;">
                <p style="margin: 0; color: #0369A1; font-size: 16px; line-height: 1.5;">
                  ${customMessage.split('\n').map(line => `${line}`).join('<br>')}
                </p>
              </div>
              ` : ''}
              
              <!-- This Week's Event -->
              <h3 style="color: #1F2937; font-size: 18px; font-weight: bold; margin: 0 0 10px 0;">⛳ This Week's Event</h3>
              <table width="100%" cellpadding="8" cellspacing="0" style="background-color: #F9FAFB; border: 1px solid #E5E7EB; border-radius: 6px;">
                <tr>
                  <td>
                    <strong style="color: #1F2937; font-size: 16px;">
                      ${eventDetails.website ? 
                        `<a href="${eventDetails.website}" style="color: #4F46E5; text-decoration: none;">${eventDetails.course_name}</a>` :
                        eventDetails.course_name
                      }
                    </strong><br>
                    <span style="color: #6B7280;">
                      ${formatDate(eventDetails.date)} • ${formatTime(eventDetails.tee_time)}<br>
                      ${eventDetails.course_address ? `📍 <a href="https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(eventDetails.course_address)}" style="color: #4F46E5; text-decoration: none;">${eventDetails.course_address}</a><br>` : ''}
                      ${eventDetails.cost ? `💵 Cost: $${Number(eventDetails.cost).toFixed(2)}` : ''}
                    </span>
                  </td>
                </tr>
              </table>
              
              ${signupsSection}
              ${upcomingSection}
              ${weatherSection}
              
              <!-- Signature -->
              <table width="100%" cellpadding="0" cellspacing="0" style="margin-top: 30px;">
                <tr>
                  <td>
                    <p style="margin: 0;">See you on the course!</p>
                    <p style="margin: 15px 0;">
                      Cheers,<br>
                      <strong>Gulf Coast Hackers League Admin</strong><br>
                      <a href="https://gulfcoasthackers.com" style="color: #4F46E5; text-decoration: none; font-style: italic;">gulfcoasthackers.com</a>
                    </p>
                  </td>
                </tr>
              </table>
              
            </td>
          </tr>
          
          <!-- Footer -->
          <tr>
            <td style="background-color: #6B7280; color: white; padding: 15px; text-align: center;">
              <p style="margin: 0; font-size: 14px;">🏌️ Gulf Coast Hackers League | Just Some Wankers Having Fun</p>
            </td>
          </tr>
          
        </table>
      </td>
    </tr>
  </table>
</body>
</html>`;
};